import React, {useRef, useState} from 'react';
import {diApi} from '@di-shared/api/di';
import {
  CreatedMaintenanceRecordRequest,
  MaintenanceNotificationResponse,
  MaintenanceNotificationWithRecordResponse,
} from '@di-shared/api/di/__generated';
import BottomSheetWebViewWrapper from '@di-shared/features/common/BottomSheetWebViewWrapper';
import SestExchangeHistorySelection from '@di-shared/features/diag/exchange/history/bottom-sheet/SestExchangeHistorySelection';
import SestExchangeHistoryNotice from '@di-shared/features/diag/exchange/history/bottom-sheet/auto/SestExchangeHistoryNotice';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface SestReplacementHistoryProps {
  vin: string;
  onExit: () => void;
}

function SestExchangeHistoryAutoBottomSheet({vin, onExit}: SestReplacementHistoryProps) {
  const {data: unreadServiceCenterNotifications} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenanceNotificationController.getUnreadServiceCenterNotifications), vin],
    queryFn: () => diApi.maintenanceNotificationController.getUnreadServiceCenterNotifications({vin}),
    select: (data) => data.data,
  });

  const [stepIndex, setStepIndex] = useState(0);
  const steps = useRef<JSX.Element[]>([]);

  function nextStep() {
    setStepIndex((stepIndex) => {
      if (stepIndex >= steps.current.length - 1) {
        onExit();
        return stepIndex + 1;
      }
      return stepIndex + 1;
    });
  }

  if (steps.current.length === 0) {
    unreadServiceCenterNotifications.forEach((item) => {
      const exchangeSestCodes = item.notifications.map((notification) => notification.sestCode as CreatedMaintenanceRecordRequest.sestCode);
      const newRecordNotification = item.notifications.filter(({type}) => type === MaintenanceNotificationResponse.type.NEW_RECORD);
      const keepRecordNotification = item.notifications.filter(({type}) => type === MaintenanceNotificationResponse.type.KEEP_RECORD);
      const comparisonNotification = item.notifications.filter(({type}) => type === MaintenanceNotificationResponse.type.COMPARISON);
      /**
       * date 기준으로 하나씩 들어가야됨 .... n 개의 date
       * 교체한 소모품
       * 브레이크 패드, 엔진오일 및 필터       2024년 5월 23일 .... m개의 소모품
       *
       */
      steps.current.push(
        <SestExchangeHistoryNotice
          onNext={async () => {
            // Promise 배열 생성
            const promises: Promise<unknown>[] = [];
            newRecordNotification.forEach(({id}) => {
              promises.push(
                diApi.maintenanceNotificationController.updateNotification2({
                  vin,
                  notificationId: id,
                  action: MaintenanceNotificationWithRecordResponse.actionTaken.RECORD_ADDED,
                })
              );
            });
            keepRecordNotification.forEach(({id}) => {
              promises.push(
                diApi.maintenanceNotificationController.updateNotification2({
                  vin,
                  notificationId: id,
                  action: MaintenanceNotificationWithRecordResponse.actionTaken.PENDING_DECISION,
                })
              );
            });
            // 모든 요청 완료 후 다음 스텝으로
            await Promise.all(promises);
            nextStep();
          }}
          exchangeDate={parseDateWithFormat(item.date)}
          exchangeSestCodes={exchangeSestCodes}
        />
      );

      /**
       * date 하나에 comparison인 소모품을 넣어야됨
       */
      comparisonNotification.forEach((record) => {
        steps.current.push(
          <SestExchangeHistorySelection
            key={item.date + record.id}
            notificationId={record.id}
            vin={vin}
            onNext={nextStep}
            records={[record.newRecord, record.existRecord]}
          />
        );
      });
    });
  }
  return (
    <BottomSheetWebViewWrapper bottomSheetFallbackHeight={300}>
      <div>{steps.current[stepIndex]}</div>
    </BottomSheetWebViewWrapper>
  );
}

export default SestExchangeHistoryAutoBottomSheet;
